import { Link } from "react-router-dom";
import { PublicLayout } from "../../components/public-layout.js";
import {
  HeroSection,
  FeatureCard,
  CTASection,
  TrustCallout,
} from "../../components/marketing-components.js";
import { UrlChecker } from "../../components/url-checker.js";

/* Lightweight inline SVG icons for feature cards */
const IconTemplates = (
  <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
    <rect x="3" y="3" width="7" height="7" rx="1" /><rect x="14" y="3" width="7" height="7" rx="1" /><rect x="3" y="14" width="7" height="7" rx="1" /><rect x="14" y="14" width="7" height="7" rx="1" />
  </svg>
);
const IconGrouped = (
  <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
    <line x1="18" y1="20" x2="18" y2="10" /><line x1="12" y1="20" x2="12" y2="4" /><line x1="6" y1="20" x2="6" y2="14" />
  </svg>
);
const IconRecurring = (
  <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="23 4 23 10 17 10" /><polyline points="1 20 1 14 7 14" /><path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15" />
  </svg>
);
const IconDocument = (
  <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
    <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" /><polyline points="14 2 14 8 20 8" /><line x1="16" y1="13" x2="8" y2="13" /><line x1="16" y1="17" x2="8" y2="17" />
  </svg>
);
const IconStatement = (
  <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
    <path d="M12 20h9" /><path d="M16.5 3.5a2.121 2.121 0 0 1 3 3L7 19l-4 1 1-4L16.5 3.5z" />
  </svg>
);
const IconTarget = (
  <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="12" cy="12" r="10" /><circle cx="12" cy="12" r="6" /><circle cx="12" cy="12" r="2" />
  </svg>
);

export function PublicHomePage() {
  return (
    <PublicLayout>
      <HeroSection
        title="Continuous accessibility monitoring for the web"
        subtitle="Keep your website accessible. Track changes. Generate reports and accessibility statements automatically."
      >
        <UrlChecker />
      </HeroSection>

      <section className="marketing-section">
        <div className="section-content">
          <h2>Why accessibility monitoring?</h2>
          <p>
            One-off accessibility audits miss the ongoing changes. Our platform
            monitors your site month-over-month, detects new issues, and tracks
            your progress.
          </p>
        </div>

        <div className="features-grid">
          <FeatureCard
            icon={IconTemplates}
            title="Template-aware scanning"
            description="Smart detection of your site structure, templates, and navigation patterns for accurate, focused results."
          />
          <FeatureCard
            icon={IconGrouped}
            title="Grouped issue reporting"
            description="Issues grouped by type and page, with element highlights and evidence. No overwhelming lists."
          />
          <FeatureCard
            icon={IconRecurring}
            title="Recurring scans"
            description="Schedule scans weekly, monthly, or at your own pace. See which issues are new, fixed, or persisting."
          />
          <FeatureCard
            icon={IconDocument}
            title="Document scanning"
            description="Detect and scan PDFs and documents separately. Includes PDF accessibility analysis."
          />
          <FeatureCard
            icon={IconStatement}
            title="Statement generator"
            description="Auto-generate accessibility statements with your actual findings and remediation timelines."
          />
          <FeatureCard
            icon={IconTarget}
            title="Confidence scoring"
            description="Honest scores showing what automated testing found. We tell you what manual review still matters."
          />
        </div>
      </section>

      <section className="marketing-section marketing-section--alt">
        <div className="section-content">
          <h2>Honest about limitations</h2>
          <TrustCallout>
            <p>
              Automated testing catches many issues—but not all. We're
              transparent about confidence levels and clearly mark where manual
              review adds real value.
            </p>
            <p>
              <Link to="/methodology">Learn our approach →</Link>
            </p>
          </TrustCallout>
        </div>
      </section>

      <section className="marketing-section">
        <div className="section-content">
          <h2>How it works</h2>
          <ol className="how-it-works-list">
            <li>
              <strong>Add your site</strong> — Verify ownership via DNS or meta
              tag
            </li>
            <li>
              <strong>Configure scan</strong> — Choose scan depth and frequency
              through a guided setup
            </li>
            <li>
              <strong>First scan</strong> — We detect your site structure and
              scan representative pages automatically
            </li>
            <li>
              <strong>Review findings</strong> — Grouped issues with screenshots
              and evidence
            </li>
            <li>
              <strong>Generate reports</strong> — Accessibility statement,
              detailed audit, documents
            </li>
            <li>
              <strong>Track progress</strong> — Re-scan over time to see what
              has improved and what needs attention
            </li>
          </ol>
          <div className="text-center mt-4">
            <Link to="/how-it-works" className="btn btn--secondary">
              See full walk-through
            </Link>
          </div>
        </div>
      </section>

      <section className="marketing-section marketing-section--alt">
        <div className="section-content">
          <h2>Plans that grow with you</h2>
          <p>
            Start free. Scale as you monitor more sites, scan more documents,
            and gain more confidence in your accessibility progress.
          </p>
          <div className="text-center mt-4">
            <Link to="/plans" className="btn btn--primary">
              View all plans
            </Link>
          </div>
        </div>
      </section>

      <CTASection
        title="Monitor your accessibility today"
        description="Start monitoring for free. No credit card required."
        primaryCTA={{ label: "Sign up free", href: "/sign-in" }}
        secondaryCTA={{ label: "Book a demo", href: "/contact" }}
      />
    </PublicLayout>
  );
}
